import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { VisualSettings } from '@DStypes/chart';

import {
  CloseButton,
  CustomSelect,
  FaIcon,
  FilterItem,
  MDBox,
  MDButton,
  MDTypography
} from '@components';
import {
  Button,
  Dialog,
  DialogContent,
  Grid,
  Link,
  Switch,
  Tooltip
} from '@mui/material';
import {
  useGetChartDetailsQuery,
  useUpdateChartDynamicFiltersMutation,
  useUpdateChartGlobalFiltersMutation
} from '@api/chart';
import {
  ColumnItemDynamicFilter,
  ColumnItemList,
  ColumnItemRange,
  FilterType,
  IGlobalFilters,
  isActiveFilter
} from '@DStypes/filter';
import { otherDataTypes } from '@utils/common';
import { updateConfig } from '@pages/EditChart/slice';
import { MSG_EVENT, MSG_TYPE, NotificationData } from '@DStypes/notifications';
import { setUserNotifications } from '@components/Notification/slice';
import { RootState } from '@store/store';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  ADDED_CHART_FILTER,
  CLICKED_CHART_FILTER,
  CLICKED_KNOW_MORE,
  REMOVED_CHART_FILTER
} from '@constants/eventNames';
import { numberEquivalents, textEquivalents } from '@constants/common';

interface IFilterProps {
  chartFilters: IGlobalFilters;
  dynamicFilters: IGlobalFilters;
  projectBoardFilters: IGlobalFilters;
  projectBoardDynamicFilters: IGlobalFilters;
  projectId: string;
  visualSettings: VisualSettings | null;
  setVisualSettings: React.Dispatch<
    React.SetStateAction<VisualSettings | null>
  >;
  chartRefetchFunction: () => void;
  xAxisLabel: string;
  iconTooltipText: string;
  dateGroup: string;
  setRefetchChart: React.Dispatch<React.SetStateAction<boolean>>;
  setIsManuallySetDashboardFilterToggle: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}

const GlobalFilters: React.FC<IFilterProps> = ({
  chartFilters,
  dynamicFilters,
  projectId,
  projectBoardFilters,
  projectBoardDynamicFilters,
  visualSettings,
  setVisualSettings,
  // chartRefetchFunction,
  xAxisLabel,
  iconTooltipText,
  dateGroup,
  setRefetchChart,
  setIsManuallySetDashboardFilterToggle
}) => {
  const { chartId } = useParams();

  const currentChartData = useSelector(
    (state: RootState) => state?.currentChartData
  );

  const { data: dropDownData } = useGetChartDetailsQuery({
    id: projectId as string
  });
  const dispatch = useDispatch();

  const [updateChartGlobalFilters] = useUpdateChartGlobalFiltersMutation();
  const [updateChartDynamicFilters] = useUpdateChartDynamicFiltersMutation();

  const [isDashFilterActive, setDashFilterActive] = useState(
    !!visualSettings?.toggleDashboardFilter
  );
  const [selectedColumn, setSelectedColumn] = useState('');
  const [chartGlobalFilters, setChartGlobalFilters] = useState(
    chartFilters || {}
  );

  const [chartDynamicFilters, setChartDynamicFilters] = useState(
    dynamicFilters || {}
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [projectGlobalFilters, setProjectGlobalFilters] = useState(
    projectBoardFilters || {}
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [projectDynamicFilters, setProjectDynamicFilters] = useState(
    projectBoardDynamicFilters || {}
  );

  const [isCopyFilterModalOpen, setCopyFilterModalOpen] = useState(false);
  const [isClearAllFilterModalOpen, setClearAllFilterModalOpen] =
    useState(false);
  const [isCopyingDueToAddition, setCopyingDueToAddition] = useState(false);
  const [isClearChartFilterModalOpen, setClearChartFilterModalOpen] =
    useState(false);
  const [filterToBeAdded, setFilterToBeAdded] = useState<any>({
    filter: {},
    dynamic_filter: false
  });

  const chartFilterColumns = useMemo(
    () => Object.keys(chartGlobalFilters),
    [chartGlobalFilters]
  );

  const chartDynamicFilterColumns = useMemo(
    () => Object.keys(chartDynamicFilters),
    [chartDynamicFilters]
  );

  const projectFilterColumns = useMemo(
    () => Object.keys(projectGlobalFilters),
    [projectGlobalFilters]
  );

  const projectDynamicFilterColumns = useMemo(
    () => Object.keys(projectDynamicFilters),
    [projectDynamicFilters]
  );

  const allProjectFilterColumns = useMemo(
    () => projectFilterColumns.concat(projectDynamicFilterColumns),
    [projectFilterColumns, projectDynamicFilterColumns]
  );

  const [addFilter, setAddFilter] = useState(false);

  // To show dropdown directly if there are are no existing filters
  useEffect(() => {
    if (!chartFilterColumns.length && !chartDynamicFilterColumns.length)
      setAddFilter(true);
  }, [chartFilterColumns, chartDynamicFilterColumns]);

  const dropDownOptions = useMemo(() => {
    if (dropDownData) {
      const filterColumns = !visualSettings?.toggleDashboardFilter
        ? Object.keys({ ...chartGlobalFilters, ...chartDynamicFilters })
        : Object.keys({
            ...chartGlobalFilters,
            ...chartDynamicFilters,
            ...projectGlobalFilters,
            ...projectDynamicFilters
          });

      const xAxisOption = dropDownData.available_fields.find(
        (field: any) => field.label === xAxisLabel
      );

      let axisIndex = -1;

      if (visualSettings?.toggleDashboardFilter)
        axisIndex = filterColumns.findIndex(
          (column) => column === xAxisOption?.label
        );
      else
        axisIndex = Object.keys({
          ...chartGlobalFilters,
          ...chartDynamicFilters
        }).findIndex((column) => column === xAxisOption?.label);

      const options = [xAxisOption, ...dropDownData.available_fields]
        .map((obj: any) => {
          if (obj?.label === xAxisLabel) return;

          if (!filterColumns.includes(obj?.label)) return obj;
        })
        .filter((obj: any) => obj !== undefined);

      return axisIndex === -1 ? [xAxisOption, ...options] : options;
    }

    return [];
  }, [
    chartGlobalFilters,
    chartDynamicFilters,
    dropDownData,
    visualSettings?.toggleDashboardFilter
  ]);
  const isInitialLoad = useRef(true);

  const getActiveChartFilters = (): string[] => {
    const activeChartFilters: string[] = [];

    chartFilterColumns.forEach((columnName) => {
      if (chartGlobalFilters[columnName].active === isActiveFilter.TRUE)
        activeChartFilters.push(columnName);
    });

    chartDynamicFilterColumns.forEach((columnName) => {
      if (chartDynamicFilters[columnName].active === isActiveFilter.TRUE)
        activeChartFilters.push(columnName);
    });

    return activeChartFilters;
  };

  //Hook to turn off dashBoard filter toggle if chart filters are enabled on initial load
  useEffect(() => {
    if (isInitialLoad.current)
      if (getActiveChartFilters().length) {
        setVisualSettings((prev) => ({
          ...prev,
          toggleDashboardFilter: false
        }));

        setDashFilterActive(false);
      }

    isInitialLoad.current = false;
  }, [chartGlobalFilters, chartDynamicFilters]);

  // Hook to switch on global filter on mount if there are chart level filters
  useEffect(() => {
    if (
      // (visualSettings?.toggleDashboardFilter &&
      // projectFilterColumns?.length) ||
      (chartFilterColumns.length > 0 || chartDynamicFilterColumns.length > 0) &&
      String(visualSettings?.chartGlobalFilterToggle) !== 'false'
    )
      setVisualSettings((prev) => ({ ...prev, chartGlobalFilterToggle: true }));
  }, []);

  // Hook to switch on/off toggleDashboard filter based on filter connection
  useEffect(() => {
    if (
      visualSettings?.toggleDashboardFilter &&
      (chartFilterColumns.length > 0 || chartDynamicFilterColumns.length > 0)
    )
      setVisualSettings((prev) => ({ ...prev, toggleDashboardFilter: false }));
    // if (
    //   visualSettings?.chartGlobalFilterToggle &&
    //   !visualSettings?.toggleDashboardFilter &&
    //   chartFilterColumns.length === 0
    // )
    // setVisualSettings((prev) => ({ ...prev, toggleDashboardFilter: true }));
  }, [chartFilterColumns, chartDynamicFilterColumns]);

  // Hook to toggle chart filter off if no filters present
  // useEffect(() => {
  //   if (
  //     !visualSettings?.toggleDashboardFilter &&
  //     chartFilterColumns.length === 0 &&
  //     visualSettings?.chartGlobalFilterToggle
  //   )
  //     setVisualSettings((prev) => ({
  //       ...prev,
  //       chartGlobalFilterToggle: false
  //     }));
  // }, [chartFilterColumns, projectFilterColumns]);

  const sendNotification = (msg_event: MSG_EVENT, message: string) => {
    const payload: NotificationData = {
      created_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      is_read: false,
      is_silent: true,
      is_user_message: true,
      message_event: msg_event,
      message_text: message,
      updated_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      message_type: MSG_TYPE.SUCCESS,
      message_data: JSON.stringify({
        chart_id: currentChartData.id,
        project: { id: '', name: currentChartData.chart_project_name }
      }),
      linkKeyword: 'here.',
      replaceKeyword: '<click here>',
      is_important: false,
      recipient_obj: 0,
      isAnchorElement: true
    };

    dispatch(setUserNotifications(payload));
  };

  const handleClearFiltersApi = async () => {
    setChartDynamicFilters({});
    setChartGlobalFilters({});
    setAddFilter(false);
    setSelectedColumn('');
    dispatch(
      updateConfig({
        field: 'dynamic_filters',
        value: {}
      })
    );
    dispatch(
      updateConfig({
        field: 'filters',
        value: {}
      })
    );

    if (Object.keys(chartDynamicFilters)?.length)
      await updateChartDynamicFilters({
        chart_id: chartId || '',
        payload: { dynamic_filters: {} }
      });

    if (Object.keys(chartGlobalFilters)?.length)
      await updateChartGlobalFilters({
        chart_id: chartId || '',
        payload: { filters: {} }
      });

    setRefetchChart(true);
    setFilterToBeAdded({});

    // FIXME: debounced update of config call, so need to refetch chart in this case alone
    // setTimeout(() => chartRefetchFunction(), 1000);
  };

  const handleForceCopyApi = async (forceCopy: boolean) => {
    const modifiedChartGlobalFilters = {
      ...(forceCopy && projectGlobalFilters),
      ...(!filterToBeAdded.dynamic_filter && filterToBeAdded.filter)
    };

    const modifiedChartDynamicFilters = {
      ...(forceCopy && projectDynamicFilters),
      ...(filterToBeAdded.dynamic_filter && filterToBeAdded.filter)
    };

    setChartDynamicFilters(modifiedChartDynamicFilters);
    setChartGlobalFilters(modifiedChartGlobalFilters);
    setAddFilter(false);
    setSelectedColumn('');
    dispatch(
      updateConfig({
        field: 'dynamic_filters',
        value: modifiedChartDynamicFilters
      })
    );
    dispatch(
      updateConfig({
        field: 'filters',
        value: modifiedChartGlobalFilters
      })
    );
    await updateChartDynamicFilters({
      chart_id: chartId || '',
      payload: { dynamic_filters: modifiedChartDynamicFilters }
    });

    await updateChartGlobalFilters({
      chart_id: chartId || '',
      payload: { filters: modifiedChartGlobalFilters }
    });
    setRefetchChart(true);
    setFilterToBeAdded({ filter: {}, dynamic_filter: false });

    // FIXME: debounced update of config call, so need to refetch chart in this case alone
    // setTimeout(() => chartRefetchFunction(), 1000);
  };

  const handleFilterSubmit = async (
    newFilters: IGlobalFilters,
    dynamic_filter: boolean
  ) => {
    const columnTypes = Object.keys(newFilters).map((columnName: string) =>
      getColumnType(columnName)
    );

    const dataTypes = columnTypes.map((column: string) => {
      if (textEquivalents.includes(column)) return 'TEXT';
      else if (numberEquivalents.includes(column)) return 'NUMBER';
      else return 'DATE';
    });

    if (columnTypes.length)
      sendAmplitudeData(ADDED_CHART_FILTER, {
        'Data Type': dataTypes.join(', '),
        'Filter Count(before)':
          chartFilterColumns.length + chartDynamicFilterColumns.length,
        'Dashboard Filter applied': isDashFilterActive
      });

    const newFilterKeys = Object.keys(newFilters);

    if (dynamic_filter)
      newFilterKeys.map((key: string) => {
        if (chartFilterColumns.includes(key))
          handleDeleteFilter(key, false, false);
      });
    else
      newFilterKeys.map((key: string) => {
        if (chartDynamicFilterColumns.includes(key))
          handleDeleteFilter(key, false, true);
      });

    let modifiedFilter = {};

    if (dynamic_filter)
      modifiedFilter = { ...chartDynamicFilters, ...newFilters };
    else modifiedFilter = { ...chartGlobalFilters, ...newFilters };

    if (!isDashFilterActive || !allProjectFilterColumns?.length) {
      if (dynamic_filter) setChartDynamicFilters(modifiedFilter);
      else setChartGlobalFilters(modifiedFilter);
      setAddFilter(false);
      setSelectedColumn('');
      dispatch(
        updateConfig({
          field: dynamic_filter ? 'dynamic_filters' : 'filters',
          value: modifiedFilter
        })
      );
      if (dynamic_filter)
        await updateChartDynamicFilters({
          chart_id: chartId || '',
          payload: { dynamic_filters: modifiedFilter }
        });
      else
        await updateChartGlobalFilters({
          chart_id: chartId || '',
          payload: { filters: modifiedFilter }
        });

      setRefetchChart(true);
      setFilterToBeAdded({ filter: {}, dynamic_filter: false });

      // FIXME: debounced update of config call, so need to refetch chart in this case alone
      // setTimeout(() => chartRefetchFunction(), 1000);
    } else {
      setFilterToBeAdded({
        filter: newFilters,
        dynamic_filter: dynamic_filter
      });
      setCopyFilterModalOpen(true);
      setCopyingDueToAddition(true);
    }
  };

  const handleColumnToggleChange = async (
    value: boolean,
    columnName: string,
    dynamic_filter: boolean
  ) => {
    if (!value) {
      const columnType = getColumnType(columnName);

      sendAmplitudeData(REMOVED_CHART_FILTER, {
        'Data Type': textEquivalents.includes(columnType)
          ? 'TEXT'
          : numberEquivalents.includes(columnType)
          ? 'NUMBER'
          : 'DATE'
      });

      sendNotification(
        MSG_EVENT.CHART_FILTER_REMOVED,
        `"${columnName}" Filter has been removed for ${currentChartData.chart_title}`
      );
    } else {
      sendNotification(
        MSG_EVENT.CHART_FILTER_APPLIED,
        `You have successfully filtered the chart ${currentChartData.chart_title} by “${columnName}”`
      );
    }

    if (dynamic_filter) {
      const modifiedFilter = {
        ...chartDynamicFilters,
        [columnName]: {
          ...chartDynamicFilters[columnName],
          active: value ? isActiveFilter.TRUE : isActiveFilter.FALSE
        }
      };

      setChartDynamicFilters(modifiedFilter as IGlobalFilters);
      dispatch(
        updateConfig({
          field: 'dynamic_filters',
          value: modifiedFilter
        })
      );

      await updateChartDynamicFilters({
        chart_id: chartId || '',
        payload: { dynamic_filters: modifiedFilter }
      });
      setRefetchChart(true);
    } else {
      const modifiedFilter = {
        ...chartGlobalFilters,
        [columnName]: {
          ...chartGlobalFilters[columnName],
          active: value ? isActiveFilter.TRUE : isActiveFilter.FALSE
        }
      };

      setChartGlobalFilters(modifiedFilter as IGlobalFilters);
      dispatch(
        updateConfig({
          field: 'filters',
          value: modifiedFilter
        })
      );

      await updateChartGlobalFilters({
        chart_id: chartId || '',
        payload: { filters: modifiedFilter }
      });
      setRefetchChart(true);
    }

    // chartRefetchFunction();
  };

  const handleDeleteFilter = async (
    key: string,
    newFilter: boolean,
    dynamic_filter: boolean
  ) => {
    if (newFilter) {
      // if (projectFilterColumns.length) setAddFilter(false);
      setSelectedColumn('');

      return;
    }
    if (dynamic_filter) {
      const currentFilter = { ...chartDynamicFilters };

      delete currentFilter[key];
      setChartDynamicFilters(currentFilter);
      dispatch(
        updateConfig({
          field: 'dynamic_filters',
          value: currentFilter
        })
      );
      await updateChartDynamicFilters({
        chart_id: chartId || '',
        payload: { dynamic_filters: currentFilter }
      });
      setRefetchChart(true);
    } else {
      const currentFilter = { ...chartGlobalFilters };

      delete currentFilter[key];
      dispatch(
        updateConfig({
          field: 'filters',
          value: currentFilter
        })
      );
      setChartGlobalFilters(currentFilter);
      await updateChartGlobalFilters({
        chart_id: chartId || '',
        payload: { filters: currentFilter }
      });
      setRefetchChart(true);
    }

    sendNotification(
      MSG_EVENT.CHART_FILTER_REMOVED,
      `"${key}" Filter has been removed for ${currentChartData.chart_title}`
    );

    const columnType = getColumnType(key);

    sendAmplitudeData(REMOVED_CHART_FILTER, {
      'Data Type': textEquivalents.includes(columnType)
        ? 'TEXT'
        : numberEquivalents.includes(columnType)
        ? 'NUMBER'
        : 'DATE'
    });

    // chartRefetchFunction();
  };

  const getColumnName = (id: string) => {
    if (dropDownData && dropDownData?.available_fields) {
      const column = dropDownData.available_fields.find(
        (obj: any) => obj.value === id
      );

      return column.label;
    }
  };

  const getColumnId = (name: string) => {
    if (dropDownData.available_fields) {
      const column = dropDownData.available_fields.find(
        (obj: any) => obj.label === name
      );

      return column.value;
    }
  };

  const handleDashBoardFilterToggle = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setIsManuallySetDashboardFilterToggle(true);
    if (!checked) return setCopyFilterModalOpen(true);

    if (chartFilterColumns.length || chartDynamicFilterColumns.length)
      return setClearChartFilterModalOpen(true);

    sendNotification(
      MSG_EVENT.CHART_FILTER_APPLIED,
      `You have successfully filtered the chart ${
        currentChartData.chart_title
      } by “${allProjectFilterColumns.join(', ')}”`
    );

    setVisualSettings((prev) => ({ ...prev, toggleDashboardFilter: true }));
    // setTimeout(() => {
    //   chartRefetchFunction();
    // }, 1000);

    return setDashFilterActive(true);
  };

  const handleFilterCopy = async () => {
    const allProjectFilterColumns = projectFilterColumns.concat(
      projectDynamicFilterColumns
    );

    sendNotification(
      MSG_EVENT.FILTERS_COPIED,
      `"${allProjectFilterColumns.join(', ')}" Filter is now applied to ${
        currentChartData.chart_title
      }`
    );

    if (filterToBeAdded && Object.keys(filterToBeAdded.filter || {}).length)
      sendNotification(
        MSG_EVENT.CHART_FILTER_APPLIED,
        `You have successfully filtered the chart ${
          currentChartData.chart_title
        } by “${Object.keys(filterToBeAdded.filter)[0]}”`
      );

    await handleForceCopyApi(true);
    setDashFilterActive(false);
    setCopyingDueToAddition(false);

    setVisualSettings((prev) => ({
      ...prev,
      chartGlobalFilterToggle: true,
      toggleDashboardFilter: false
    }));
    setCopyFilterModalOpen(false);
  };

  const handleClearChartFilters = async () => {
    const allProjectFilterColumns = projectFilterColumns.concat(
      projectDynamicFilterColumns
    );

    sendNotification(
      MSG_EVENT.FILTERS_COPIED,
      `"${allProjectFilterColumns.join(', ')}" Filter is now applied to ${
        currentChartData.chart_title
      }`
    );
    // await handleFilterSubmit({}, false, true);
    await handleClearFiltersApi();
    setClearChartFilterModalOpen(false);
    // setChartGlobalFilters({});
    setDashFilterActive(true);
    setVisualSettings((prev) => ({
      ...prev,
      chartGlobalFilterToggle: false,
      toggleDashboardFilter: true
    }));
  };

  const dashBoardColumnNames = allProjectFilterColumns.join(', ');

  const handleClearAllFilters = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (!checked) {
      if (
        !getActiveChartFilters().length
        // &&
        // !(isDashFilterActive && Boolean(projectFilterColumns?.length))
      )
        setVisualSettings((prev) => ({
          ...prev,
          chartGlobalFilterToggle: false
          // toggleDashboardFilter: false
        }));
      else setClearAllFilterModalOpen(true);

      setSelectedColumn('');
    } else {
      sendAmplitudeData(CLICKED_CHART_FILTER);
      setVisualSettings((prev) => ({
        ...prev,
        chartGlobalFilterToggle: checked
      }));
    }
  };

  const handleDisableDashboardFilter = async () => {
    const allProjectFilterColumns = projectFilterColumns.concat(
      projectDynamicFilterColumns
    );

    sendNotification(
      MSG_EVENT.FILTER_DO_NOT_COPY,
      `"${allProjectFilterColumns.join(', ')}" Filter has been removed for ${
        currentChartData.chart_title
      }`
    );

    if (Object.keys(filterToBeAdded.filter).length)
      sendNotification(
        MSG_EVENT.CHART_FILTER_APPLIED,
        `You have successfully filtered the chart ${
          currentChartData.chart_title
        } by “${Object.keys(filterToBeAdded.filter)[0]}”`
      );

    setDashFilterActive(false);
    setVisualSettings((prev) => ({
      ...prev,
      toggleDashboardFilter: false
    }));
    setCopyFilterModalOpen(false);
    // await handleFilterSubmit(filterToBeAdded, false, true);

    if (Object.keys(filterToBeAdded.filter).length)
      await handleForceCopyApi(false);

    setRefetchChart(true);
    // setTimeout(() => {
    //   chartRefetchFunction();
    // }, 1000);
  };

  const handleFilterToggleOff = async () => {
    const allFilterColumns = chartFilterColumns.concat(
      chartDynamicFilterColumns
    );
    const removedColumns =
      // visualSettings?.toggleDashboardFilter && projectFilterColumns.length
      //   ? projectFilterColumns.join(', ')
      //   :
      allFilterColumns.join(', ');

    sendNotification(
      MSG_EVENT.CHART_FILTER_REMOVED,
      `"${removedColumns}" Filter has been removed for ${currentChartData.chart_title}`
    );

    // await handleFilterSubmit({}, false, true, true);
    await handleClearFiltersApi();

    const dataTypes = allFilterColumns.map((column: string) => {
      const columnType = getColumnType(column);

      if (textEquivalents.includes(columnType)) return 'TEXT';
      else if (numberEquivalents.includes(columnType)) return 'NUMBER';
      else return 'DATE';
    });

    sendAmplitudeData(REMOVED_CHART_FILTER, {
      'Data Type': dataTypes.join(', ')
    });

    // setChartGlobalFilters({});
    setVisualSettings((prev) => ({
      ...prev,
      chartGlobalFilterToggle: false
      // toggleDashboardFilter: false
    }));
    // setDashFilterActive(false);
    setClearAllFilterModalOpen(false);

    // FIXME: debounced update of config call, so need to refetch chart in this case alone
    // setTimeout(() => chartRefetchFunction(), 1000);
  };

  const getIsDateValue = (id: string) => {
    const column = dropDownData?.available_fields?.find(
      (obj: any) => obj.value === id
    );

    if (column?.type === 'DATE' || column?.type === 'DATETIME')
      return column?.type;
    else return '';
  };

  const getFilterType = (id: string) => {
    const column = dropDownData?.available_fields?.find(
      (obj: any) => obj.value === id
    );

    if (column?.type === 'TEXT' || otherDataTypes.includes(column?.type))
      return FilterType.VALUES;

    return FilterType.RANGE;
  };

  const getColumnType = (columnName: string) => {
    if (dropDownData && dropDownData?.available_fields) {
      const column = dropDownData.available_fields.find(
        (obj: any) => obj.label === columnName
      );

      return column?.type;
    }
  };

  return (
    <>
      <MDBox maxHeight={`${400}px`} overflow='auto' minHeight='45px'>
        <Grid container justifyContent='space-between' my={1}>
          <Grid item>
            <Tooltip title={''} placement='top'>
              <Switch
                checked={!!visualSettings?.chartGlobalFilterToggle}
                onChange={handleClearAllFilters}
              />
            </Tooltip>
            <MDTypography variant='sm' color='info' ml={1} mr={0.5}>
              Add a Chart Filter
            </MDTypography>
          </Grid>
          <Grid item>
            <MDTypography
              variant='sm'
              color='secondary'
              sx={{
                textAlign: 'center',
                textUnderlineOffset: 4.5
              }}
              component={Link}
              href='https://datasquirrel.notion.site/How-to-Filter-data-on-any-column-ea549696ac7f4788ad0ff9bc917ae6e1'
              target='_blank'
              rel='noreferrer'
              underline
              fontWeight='medium'
              textGradient
            >
              <span
                role='presentation'
                onClick={() =>
                  sendAmplitudeData(CLICKED_KNOW_MORE, {
                    'Feature Name': 'Filter'
                  })
                }
              >
                How to use?
              </span>
            </MDTypography>
          </Grid>
        </Grid>
        <MDBox marginLeft={2}>
          {visualSettings?.chartGlobalFilterToggle && dropDownData && (
            <>
              {chartFilterColumns.map((column, index) => (
                <FilterItem
                  key={column}
                  index={index + 1}
                  projectId={projectId}
                  columnId={getColumnId(column)}
                  columnName={column}
                  filterType={(chartGlobalFilters?.[column] as any)?.type}
                  columnsIncluded={
                    (chartGlobalFilters?.[column] as ColumnItemList)?.values
                  }
                  sliderValues={[
                    (chartGlobalFilters?.[column] as ColumnItemRange)
                      ?.startValue,
                    (chartGlobalFilters?.[column] as ColumnItemRange)?.endValue
                  ]}
                  handleFilterSubmit={handleFilterSubmit}
                  handleDeleteFilter={handleDeleteFilter}
                  handleColumnToggleChange={handleColumnToggleChange}
                  activeStatus={chartGlobalFilters[column].active}
                  isDate={getIsDateValue(getColumnId(column))}
                  toggleDashboardFilter={
                    visualSettings.toggleDashboardFilter &&
                    Boolean(allProjectFilterColumns.length)
                  }
                  iconTooltipText={iconTooltipText}
                  dateGroup={dateGroup}
                />
              ))}
              {chartDynamicFilterColumns.map((column, index) => (
                <FilterItem
                  key={column}
                  index={index + chartFilterColumns.length + 1}
                  projectId={projectId}
                  columnId={getColumnId(column)}
                  columnName={column}
                  filterType={FilterType.DYNAMIC}
                  isDate={'DATE'}
                  handleFilterSubmit={handleFilterSubmit}
                  handleDeleteFilter={handleDeleteFilter}
                  handleColumnToggleChange={handleColumnToggleChange}
                  activeStatus={chartDynamicFilters[column].active}
                  toggleDashboardFilter={
                    visualSettings.toggleDashboardFilter &&
                    Boolean(allProjectFilterColumns.length)
                  }
                  dynamicFilterType={
                    (chartDynamicFilters[column] as any).filter
                  }
                  integerDateLimit={
                    (chartDynamicFilters[column] as ColumnItemDynamicFilter)
                      ?.number_of
                  }
                  dateGroupChoice={
                    (chartDynamicFilters[column] as ColumnItemDynamicFilter)
                      ?.group_choice
                  }
                  baselineChoice={
                    (chartDynamicFilters[column] as ColumnItemDynamicFilter)
                      ?.baseline
                  }
                  dateGroup={dateGroup}
                  iconTooltipText={iconTooltipText}
                />
              ))}
              {!addFilter && (
                <MDBox
                  display='flex'
                  component={Button}
                  alignItems='center'
                  disableRipple
                  justifyContent='center'
                  rowGap={3}
                  bordered
                  gap='10px'
                  sx={({ palette }) => ({
                    borderColor: palette.secondary.main,
                    py: 0.5,
                    width: '100%',
                    my: 2
                  })}
                  border='1px solid red'
                  onClick={() => {
                    setAddFilter(true);
                    sendAmplitudeData(CLICKED_CHART_FILTER);
                  }}
                >
                  <FaIcon
                    icon='plus'
                    typographyProps={{ variant: 'xs', color: 'secondary' }}
                  />
                  <MDTypography
                    variant='sm'
                    color='secondary'
                    fontWeight='bold'
                  >
                    Select another column and Filter
                  </MDTypography>
                </MDBox>
              )}
              {addFilter && !selectedColumn && (
                <MDBox>
                  <CustomSelect
                    placeholder='Select Column Name'
                    color='white'
                    value={selectedColumn}
                    sx={{
                      width: '100%',
                      mb: 2
                    }}
                    handleValueChange={(e: any) =>
                      setSelectedColumn(e.target.value)
                    }
                    options={dropDownOptions}
                  />
                </MDBox>
              )}
            </>
          )}
          {visualSettings?.chartGlobalFilterToggle &&
            addFilter &&
            selectedColumn && (
              <FilterItem
                projectId={projectId}
                isNewFilterItem={true}
                columnId={selectedColumn}
                columnName={getColumnName(selectedColumn)}
                filterType={getFilterType(selectedColumn)}
                columnsIncluded={[]}
                handleFilterSubmit={handleFilterSubmit}
                handleDeleteFilter={handleDeleteFilter}
                activeStatus={isActiveFilter.TRUE}
                isDate={getIsDateValue(selectedColumn)}
                toggleDashboardFilter={
                  visualSettings.toggleDashboardFilter &&
                  Boolean(allProjectFilterColumns.length)
                }
                dateGroup={dateGroup}
              />
            )}
        </MDBox>
      </MDBox>
      <MDBox
        display='flex'
        gap='4px'
        justifyContent='space-between'
        width='100%'
        sx={{ marginBottom: 1, marginTop: 1 }}
      >
        <MDBox display='flex' gap='4px'>
          <MDBox
            sx={{
              display: 'flex'
            }}
          >
            <Tooltip
              title={
                isDashFilterActive && Boolean(allProjectFilterColumns?.length)
                  ? iconTooltipText
                  : ''
              }
            >
              <Switch
                disabled={!allProjectFilterColumns?.length}
                checked={
                  isDashFilterActive && Boolean(allProjectFilterColumns?.length)
                }
                onChange={handleDashBoardFilterToggle}
              />
            </Tooltip>
          </MDBox>
          <FaIcon
            typographyProps={{
              variant: 'sm',
              color: 'info',
              marginRight: '5px',
              marginLeft: '2px',
              opacity: !allProjectFilterColumns?.length ? 0.5 : 1
            }}
            icon={['far', 'objects-column']}
          />
          <Tooltip
            title={`Dashboard Filter ${
              allProjectFilterColumns?.length && isDashFilterActive
                ? `(${dashBoardColumnNames})`
                : ''
            }`}
            placement='top'
          >
            <MDTypography
              color='info'
              fontSize='0.875rem'
              fontWeight='400'
              sx={{
                maxWidth: '235px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                opacity: !allProjectFilterColumns?.length ? 0.5 : 1
              }}
            >
              {`Dashboard Filter ${
                allProjectFilterColumns?.length && isDashFilterActive
                  ? `(${dashBoardColumnNames})`
                  : ''
              }`}
            </MDTypography>
          </Tooltip>
        </MDBox>
      </MDBox>

      {isCopyFilterModalOpen && (
        <Dialog
          open={isCopyFilterModalOpen}
          onClose={() => {
            setCopyFilterModalOpen(false);
            setCopyingDueToAddition(false);
          }}
          sx={{
            '& .MuiPaper-root': {
              width: '700px',
              maxWidth: '800px',
              position: 'relative',
              padding: '16px',
              overflow: 'visible'
            }
          }}
        >
          <CloseButton
            onClose={() => {
              setCopyFilterModalOpen(false);
              setCopyingDueToAddition(false);
            }}
          />
          <DialogContent>
            <MDBox marginBottom={2}>
              {isCopyingDueToAddition ? (
                <>
                  <MDBox mb={2}>
                    Adding this filter will disable Dashboard Filters{' '}
                    {dashBoardColumnNames}
                  </MDBox>
                  <MDBox>{`Along with ${
                    Object.keys(filterToBeAdded.filter)?.[0]
                  }, if you wish to continue to use ${dashBoardColumnNames}, then select COPY`}</MDBox>

                  <MDBox>{`Along with ${
                    Object.keys(filterToBeAdded.filter)?.[0]
                  }, if you wish to NOT use ${dashBoardColumnNames}, then select DO NOT COPY`}</MDBox>
                </>
              ) : (
                'Dashboard Filters will be disabled for this chart. If your intent was to edit them instead, then click COPY, else click DO NOT COPY'
              )}
            </MDBox>
            <MDBox display='flex' width='100%' justifyContent='center' gap={3}>
              <MDButton color='primary' onClick={handleFilterCopy}>
                COPY
              </MDButton>
              <MDButton color='primary' onClick={handleDisableDashboardFilter}>
                DO NOT COPY
              </MDButton>
            </MDBox>
          </DialogContent>
        </Dialog>
      )}

      {isClearChartFilterModalOpen && (
        <Dialog
          open={isClearChartFilterModalOpen}
          onClose={() => setClearChartFilterModalOpen(false)}
          sx={{
            '& .MuiPaper-root': {
              width: '700px',
              maxWidth: '800px',
              position: 'relative',
              overflow: 'visible',
              padding: '16px'
            }
          }}
        >
          <CloseButton onClose={() => setClearChartFilterModalOpen(false)} />
          <DialogContent>
            <MDBox marginBottom={2}>
              Chart filter {chartFilterColumns.join(', ')} will be replaced by
              Dashboard Filters. If you want to proceed, click OK.
            </MDBox>
            <MDBox display='flex' width='100%' justifyContent='center' gap={3}>
              <MDButton color='primary' onClick={handleClearChartFilters}>
                OK
              </MDButton>
              <MDButton
                color='primary'
                onClick={() => {
                  setClearChartFilterModalOpen(false);
                }}
              >
                CANCEL
              </MDButton>
            </MDBox>
          </DialogContent>
        </Dialog>
      )}

      {isClearAllFilterModalOpen && (
        <Dialog
          open={isClearAllFilterModalOpen}
          onClose={() => setClearAllFilterModalOpen(false)}
          sx={{
            '& .MuiPaper-root': {
              width: '700px',
              maxWidth: '800px',
              position: 'relative',
              overflow: 'visible',
              padding: '16px'
            }
          }}
        >
          <CloseButton onClose={() => setClearAllFilterModalOpen(false)} />
          <DialogContent>
            <MDBox marginBottom={2}>
              All filters will be removed from this chart. If you want to
              proceed, please select OK.
            </MDBox>
            <MDBox display='flex' width='100%' justifyContent='center' gap={3}>
              <MDButton color='primary' onClick={handleFilterToggleOff}>
                OK
              </MDButton>
              <MDButton
                color='primary'
                onClick={() => {
                  setClearAllFilterModalOpen(false);
                }}
              >
                CANCEL
              </MDButton>
            </MDBox>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default GlobalFilters;
